import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, User, Palmtree } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(formData.email, formData.password, formData.full_name);
      toast.success('Compte créé avec succès !');
      navigate(redirectParam || '/calculator');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-sand flex items-center justify-center py-12 px-6">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Palmtree className="w-10 h-10 text-[#005F73]" strokeWidth={1.5} />
            <span className="text-2xl font-semibold text-[#005F73]">GwadImmo Invest</span>
          </div>
          <h1 className="text-3xl font-semibold text-[#005F73] mb-2">Créer un compte</h1>
          <p className="text-stone-600">Commencez avec l'offre Découverte à 2 €</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl border border-stone-100 p-8 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="full_name">Nom complet</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-3 w-5 h-5 text-stone-400" strokeWidth={1.5} />
                <Input
                  data-testid="input-full-name"
                  id="full_name"
                  type="text"
                  required
                  placeholder="Jean Dupont"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="pl-10 h-12 rounded-xl"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-stone-400" strokeWidth={1.5} />
                <Input
                  data-testid="input-email"
                  id="email"
                  type="email"
                  required
                  placeholder="vous@exemple.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 h-12 rounded-xl"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-stone-400" strokeWidth={1.5} />
                <Input
                  data-testid="input-password"
                  id="password"
                  type="password"
                  required
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 h-12 rounded-xl"
                />
              </div>
            </div>

            <Button
              data-testid="btn-register"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-[#005F73] hover:bg-[#005F73]/90 text-lg font-medium"
            >
              {loading ? 'Création...' : 'Créer mon compte'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-stone-600">
            Vous avez déjà un compte?{' '}
            <Link to="/login" className="text-[#005F73] font-medium hover:underline">
              Se connecter
            </Link>
          </div>
        </div>

        {/* Benefits */}
        <div className="mt-8 bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-stone-200">
          <h3 className="font-medium text-[#005F73] mb-3">Offre Découverte — 2 € :</h3>
          <ul className="space-y-2 text-sm text-stone-600">
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#0A9396]"></div>
              1 simulation complète
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#0A9396]"></div>
              Calculs complets et visualisations
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#0A9396]"></div>
              Résultats détaillés et graphiques
            </li>
          </ul>
          <p className="text-xs text-stone-400 mt-3">Paiement unique — 1 seule simulation par compte. Non renouvelable.</p>
        </div>
      </div>
    </div>
  );
}
